import { z } from "zod";
import { logger } from "@/lib/logger";

/**
 * How many failed saves of one chat generation's answer the recovery tolerates
 * before it stops replaying it (LAR-543). chat-api keeps a finished generation
 * for 90 days, and every un-acked one is replayed on each app load, so an
 * answer that can never be saved used to fail, with a toast, on every visit.
 */
export const MAX_STREAM_PERSIST_ATTEMPTS = 3;

export const streamPersistAttemptsStorageKey = (userId: string): string =>
  `chat:streamPersistAttempts:${userId}`;

/** Failed saves so far, by generation id. */
const attemptsSchema = z.record(z.string(), z.number().int().positive());

type Attempts = z.infer<typeof attemptsSchema>;

const writeAttempts = (userId: string, attempts: Attempts): void => {
  const key = streamPersistAttemptsStorageKey(userId);
  try {
    if (Object.keys(attempts).length === 0) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(attempts));
  } catch {
    // Private mode / quota: see readAttempts.
  }
};

// The count lives in localStorage so it adds up across page loads. A release
// wipes it (clearAppLocalStorage), which only restarts the count.
const readAttempts = (userId: string): Attempts => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(streamPersistAttemptsStorageKey(userId));
  } catch {
    // Private mode / storage disabled: nothing is counted, so the recovery
    // keeps retrying as it did before.
    return {};
  }
  if (!raw) return {};

  let value: unknown = null;
  try {
    value = JSON.parse(raw);
  } catch {
    // Malformed JSON fails the schema below.
  }

  const parsed = attemptsSchema.safeParse(value);
  if (!parsed.success) {
    // Dropped so the warning comes once, not on every later read.
    logger.warn("Discarding unreadable chat answer save attempts", {
      operation: "readStreamPersistAttempts",
      storageKey: streamPersistAttemptsStorageKey(userId),
    });
    writeAttempts(userId, {});
    return {};
  }

  return parsed.data;
};

const recordFailure = (userId: string, generationId: string): number => {
  const attempts = readAttempts(userId);
  const failures = (attempts[generationId] ?? 0) + 1;
  writeAttempts(userId, { ...attempts, [generationId]: failures });
  return failures;
};

/** Forgets a generation's failed saves, once its answer is saved or replaced. */
export const clearStreamPersistFailures = (
  userId: string,
  generationId: string
): void => {
  const attempts = readAttempts(userId);
  if (!(generationId in attempts)) return;

  const remaining = { ...attempts };
  delete remaining[generationId];
  writeAttempts(userId, remaining);
};

export type StreamPersistFailure = {
  userId: string;
  conversationId: string;
  generationId: string;
  /** Saves the plain failed-answer message; resolves whether the API took it. */
  saveFailedAnswer: () => Promise<boolean>;
  /** Acks the generation and clears its recovery state, so it is not replayed. */
  release: () => Promise<void>;
};

/**
 * Call after a save of the generation's answer failed. Below the cap the
 * caller retries as usual ("retry"). At the cap, the failed-answer message
 * takes the answer's place and the generation is released ("replaced"). If
 * the API refuses that message too, it is probably down, so nothing is
 * discarded and the next visit tries again ("retry").
 */
export const handleStreamPersistFailure = async ({
  userId,
  conversationId,
  generationId,
  saveFailedAnswer,
  release,
}: StreamPersistFailure): Promise<"retry" | "replaced"> => {
  const failures = recordFailure(userId, generationId);
  if (failures < MAX_STREAM_PERSIST_ATTEMPTS) return "retry";

  const context = {
    operation: "handleStreamPersistFailure",
    conversationId,
    generationId,
    failures,
  };

  let saved = false;
  let saveError: unknown;
  try {
    saved = await saveFailedAnswer();
  } catch (error) {
    saveError = error;
  }
  if (!saved) {
    logger.error(
      "Failed to save the failed-answer message in place of a chat answer",
      { ...context, error: saveError }
    );
    return "retry";
  }

  logger.warn("Stopped retrying a chat answer that failed to save", context);
  clearStreamPersistFailures(userId, generationId);
  await release();
  return "replaced";
};
